// src/App.jsx
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { BottomNav } from "./components/UI";

import PlanPage    from "./pages/PlanPage";
import ChatPage    from "./pages/ChatPage";
import WineryManagerPage from "./pages/WineryManagerPage";
import RoadRepPage from './pages/RoadRepPage';
import RoadRepLoginPage from './pages/RoadRepLoginPage';
import AdminPage from './pages/AdminPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SlotPrefillApprovePage from './pages/SlotPrefillApprovePage';
import LandingPage from './pages/LandingPage';
import LoginPage from "./pages/manage/LoginPage";
import SignupPage from "./pages/manage/SignupPage";
import TravelerLoginPage from "./pages/traveler/LoginPage";
import TravelerSignupPage from "./pages/traveler/SignupPage";
import ToursPage   from "./pages/ToursPage";
import BookingsPage from "./pages/BookingsPage";
import AccountPage from "./pages/AccountPage";
import TourResultsPage from "./pages/TourResultsPage";
import WineryPage  from "./pages/WineryPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage   from "./pages/TermsPage";
import DeleteGuestEmailPage from "./pages/DeleteGuestEmailPage";
import FloatingChat from "./components/FloatingChat";

// Note: this app's real auth is a custom JWT/Postgres system (AuthContext.jsx),
// the same one winery-owner/road-rep/admin use, now extended to travelers.
// Sessions are scoped per role (localStorage "wr_token_<role>", see
// utils/authStorage.js) so a winery-owner login and a traveler login can
// coexist in the same browser. There is a separate Cognito User Pool defined
// in infra (edge.yaml) that was never wired to any API route; it's unused
// and intentionally not initialized here (see api.js).

function TravelerLayout({ children }) {
  return (
    <div className="max-w-lg mx-auto relative min-h-screen">
      {children}
      <BottomNav />
    </div>
  );
}

// GDPR/ePrivacy note: the only browser storage this app uses is
// localStorage holding the auth JWT (see utils/authStorage.js) — strictly
// necessary to keep you signed in, so it doesn't require consent. This
// banner just discloses it, dismissed once and remembered.
function StorageNotice() {
  const [dismissed, setDismissed] = React.useState(() => localStorage.getItem("wr_storage_notice_seen") === "1");
  if (dismissed) return null;
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
      background: "#1E2C14", color: "#EDF1E4", fontSize: 11, fontFamily: "'Inter',sans-serif",
      padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap",
    }}>
      <span>
        We use your browser's local storage only to keep you signed in — no tracking or advertising cookies. See our{" "}
        <a href="/privacy" style={{ color: "#C4D0A8" }}>Privacy Policy</a>.
      </span>
      <button
        onClick={() => { localStorage.setItem("wr_storage_notice_seen", "1"); setDismissed(true); }}
        style={{ background: "#3D4A1A", border: "none", borderRadius: 6, color: "#EDF1E4", fontSize: 11, padding: "4px 10px", cursor: "pointer" }}
      >
        Got it
      </button>
    </div>
  );
}

function MobileRedirect() {
  const isMobile = window.innerWidth < 768;
  return isMobile
    ? <Navigate to="/plan" replace />
    : <LandingPage />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Traveler routes */}
          <Route path="/plan" element={
            <TravelerLayout><PlanPage /></TravelerLayout>
          } />
          <Route path="/chat" element={
            <TravelerLayout><ChatPage /></TravelerLayout>
          } />
          <Route path="/tours" element={
            <TravelerLayout><ToursPage /></TravelerLayout>
          } />
          <Route path="/bookings" element={
            <TravelerLayout><BookingsPage /></TravelerLayout>
          } />
          <Route path="/account" element={
            <TravelerLayout><AccountPage /></TravelerLayout>
          } />

          <Route path="/results" element={<TourResultsPage />} />
          <Route path="/manage" element={<WineryManagerPage />} />
          <Route path="/road-rep" element={<RoadRepPage />} />
          <Route path="/road-rep/login" element={<RoadRepLoginPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/slot-prefill/approve" element={<SlotPrefillApprovePage />} />
          <Route path="/manage/login" element={<LoginPage />} />
          <Route path="/manage/signup" element={<SignupPage />} />
          <Route path="/tourist/login" element={<TravelerLoginPage />} />
          <Route path="/tourist/signup" element={<TravelerSignupPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/booking/delete-email" element={<DeleteGuestEmailPage />} />
          {/* Winery owner portal — no bottom nav */}
          <Route path="/winery/*" element={
            <div className="max-w-lg mx-auto">
              <WineryPage />
            </div>
          } />

          {/* Default */}
          <Route path="/" element={<MobileRedirect />} />
          <Route path="*" element={<Navigate to="/plan" replace />} />
        </Routes>
      <FloatingChat />
      <StorageNotice />
      </BrowserRouter>
      </AuthProvider>
  );
}
export default App;
